import { CalculateTranslationFromOpticalCenter } from "./types";

// Calculates the translation vector from the optical center.
// Inverse of: C = -Rᵀ · t
// Formula used: t = -R · C
export const calculateTranslationFromOpticalCenter: CalculateTranslationFromOpticalCenter =
  ({ rotation, opticalCenter }) => {
    const [rx, ry, rz] = rotation;
    const [cx, cy, cz] = opticalCenter;

    // Compute the rotation angle from the rotation vector
    const angle = Math.sqrt(rx * rx + ry * ry + rz * rz);

    // If the angle is very small, rotation is effectively the identity
    if (angle < 1e-8) {
      return [-cx, -cy, -cz];
    }

    // Normalize the rotation axis
    const ax = rx / angle;
    const ay = ry / angle;
    const az = rz / angle;

    // Construct the rotation matrix using Rodrigues' formula
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);
    const oneMinusCos = 1 - cosAngle;

    const R11 = cosAngle + ax * ax * oneMinusCos;
    const R12 = ax * ay * oneMinusCos - az * sinAngle;
    const R13 = ax * az * oneMinusCos + ay * sinAngle;

    const R21 = ay * ax * oneMinusCos + az * sinAngle;
    const R22 = cosAngle + ay * ay * oneMinusCos;
    const R23 = ay * az * oneMinusCos - ax * sinAngle;

    const R31 = az * ax * oneMinusCos - ay * sinAngle;
    const R32 = az * ay * oneMinusCos + ax * sinAngle;
    const R33 = cosAngle + az * az * oneMinusCos;

    // Compute t = -R · C
    const tx = -(R11 * cx + R12 * cy + R13 * cz);
    const ty = -(R21 * cx + R22 * cy + R23 * cz);
    const tz = -(R31 * cx + R32 * cy + R33 * cz);

    return [tx, ty, tz];
  };
